import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db, auth, messaging } from '../firebase';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged
} from "firebase/auth";
import {
  collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp,
  onSnapshot, updateDoc, orderBy, arrayUnion, limit as queryLimit, runTransaction
} from 'firebase/firestore';
import { getToken, onMessage } from 'firebase/messaging';
import {
  PLATFORM_WA, CLIENT_ORDER_LIMIT, WORKER_MARKET_LIMIT, ADMIN_ORDER_LIMIT,
  INVOICE_TAX_RATE, PANAMA_ZONES,
  translations, defaultLegalText,
  createEmptyInvoiceDetails, toMoney, calculateOrderSettlement, getOrderCustomerTotal,
  generateID, normalizeServiceKey, normalizeString, openWhatsApp,
  compressImage as compressImageUtil, getWorkerReviewStatus, getWorkerReviewTone,
  getStatusTone, statusMap
} from '../constants';

const AppContext = createContext(null);

export function useAppContext() {
  return useContext(AppContext);
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState('es');
  const t = translations[lang];

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const [services, setServices] = useState([]);
  const [banners, setBanners] = useState({});
  const [orders, setOrders] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);

  const [view, setView] = useState('home');
  const [filterStatus, setFilterStatus] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const [clientPhotos, setClientPhotos] = useState([]);
  const [clientMsg, setClientMsg] = useState("");
  const [suggestionText, setSuggestionText] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [payMethod, setPayMethod] = useState(null);
  const [orderInvoiceRequired, setOrderInvoiceRequired] = useState(false);
  const [orderInvoiceDetails, setOrderInvoiceDetails] = useState(createEmptyInvoiceDetails());
  const [clientZone, setClientZone] = useState(null);          // { id, label }
  const [orderIsUrgent, setOrderIsUrgent] = useState(false);
  const [orderScheduleDay, setOrderScheduleDay] = useState('today'); // 'today' | 'tomorrow' | 'custom'
  const [orderScheduleDate, setOrderScheduleDate] = useState('');    // YYYY-MM-DD for custom
  const [orderScheduleSlot, setOrderScheduleSlot] = useState(null);  // 'morning'|'afternoon'|'evening'
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [uploading, setUploading] = useState(false);
  const [workerPhotoDrafts, setWorkerPhotoDrafts] = useState({});
  const [reviewComment, setReviewComment] = useState("");
  const [rating, setRating] = useState(5);
  const [workerMaterialsCost, setWorkerMaterialsCost] = useState("");
  const [workerMaterialsNote, setWorkerMaterialsNote] = useState("");
  const [workerMaterialsReceipt, setWorkerMaterialsReceipt] = useState(null); // base64 收据照片

  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [newPromoCode, setNewPromoCode] = useState("");
  const [newPromoDiscount, setNewPromoDiscount] = useState("");

  const [editData, setEditData] = useState({
    firstName: "", lastName: "", phone: "", address: "",
    accName: "", accNum: "", accType: "ahorro",
    reference: "", idPhoto: null, selfie: null, avatar: null, skills: [],
    invoiceTermsAccepted: false, canIssueInvoice: false,
    invoiceName: "", invoiceRuc: "", invoiceDv: "", coverageZones: []
  });

  const isAdmin = user?.email === 'admin@fixbox.com';
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const [allClients, setAllClients] = useState([]);

  const [authMode, setAuthMode] = useState(null);
  const [postAuthAction, setPostAuthAction] = useState(null);
  const [authPhone, setAuthPhone] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authPasswordConfirm, setAuthPasswordConfirm] = useState('');
  const [regData, setRegData] = useState({ fn: '', ln: '' });
  const [referralInput, setReferralInput] = useState(() => {
    // 自动读取 URL ?ref=CODE
    try { return new URLSearchParams(window.location.search).get('ref') || ''; }
    catch { return ''; }
  });
  const [workerInvoiceTermsAccepted, setWorkerInvoiceTermsAccepted] = useState(false);
  const [grabConfirmOrderId, setGrabConfirmOrderId] = useState(null); // 发票二次确认

  const [previewImage, setPreviewImage] = useState(null);
  const [dashboardTime, setDashboardTime] = useState('all');
  const [adminOrderFilter, setAdminOrderFilter] = useState('all');
  const [adminOrderSearch, setAdminOrderSearch] = useState('');

  const [newServiceName, setNewServiceName] = useState("");
  const [newServicePrice, setNewServicePrice] = useState("");

  const [toast, setToast] = useState(null);

  // ---- 全局对话框（替代 window.confirm / window.prompt）----
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, msg, resolveFn }
  const [promptDialog, setPromptDialog] = useState(null);   // { title, placeholder, type, resolveFn }
  const [promptInput, setPromptInput] = useState('');

  const showConfirm = (title, msg) => new Promise((resolve) => {
    setConfirmDialog({ title, msg, resolveFn: resolve });
  });

  const showPrompt = (title, placeholder = '', options = {}) => new Promise((resolve) => {
    setPromptInput('');
    setPromptDialog({ title, placeholder, type: options.type || 'text', resolveFn: resolve });
  });

  const [showSplash, setShowSplash] = useState(true);
  const [fadeSplash, setFadeSplash] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installDevice, setInstallDevice] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const [legalContent, setLegalContent] = useState(defaultLegalText);
  const [adminLegal, setAdminLegal] = useState(defaultLegalText);
  const [yappySimulating, setYappySimulating] = useState(false);
  const [yappyPhone, setYappyPhone] = useState("");
  const [yappySuccessData, setYappySuccessData] = useState(null);

  const [currentTime, setCurrentTime] = useState(new Date());
  const workerSkillKey = (userData?.skills || []).join('|');

  // ---- 工具 ----
  const showToast = (title, desc) => {
    setToast({ title, desc });
    setTimeout(() => setToast(null), 5000);
  };

  const compressImage = (file, callback) => compressImageUtil(file, callback, lang, (msg) => showToast(lang === 'es' ? 'Error de imagen' : '图片错误', msg));

  const resetOrderDraft = () => {
    setQuantity(1); setClientPhotos([]); setClientMsg(""); setPayMethod(null);
    setAppliedPromo(null); setPromoInput(""); setOrderInvoiceRequired(false);
    setOrderInvoiceDetails(createEmptyInvoiceDetails());
  };

  const closeAuthFlow = () => {
    setAuthMode(null); setPostAuthAction(null);
    if (!user) { setRole(null); setView('home'); }
  };

  const openLoginFlow = (action = { type: 'view', view: 'profile' }) => {
    if (!user) setRole(null);
    setPostAuthAction(action); setAuthMode('login');
  };

  const openRoleEntry = (action = { type: 'view', view: 'profile' }) => {
    if (user && role) { setView(action.view || 'profile'); return; }
    setRole(null); setWorkerInvoiceTermsAccepted(false);
    setPostAuthAction(action); setAuthMode('role');
  };

  const beginRegistrationAs = (nextRole, action = null) => {
    setRole(nextRole); setWorkerInvoiceTermsAccepted(false);
    setPostAuthAction(action || (nextRole === 'worker' ? { type: 'view', view: 'editProfile' } : { type: 'view', view: 'profile' }));
    setAuthMode('name');
  };

  const chooseAuthRole = (nextRole) => {
    if (nextRole === 'worker') { beginRegistrationAs('worker', { type: 'view', view: 'editProfile' }); return; }
    beginRegistrationAs('client', postAuthAction || { type: 'view', view: 'profile' });
  };

  const resolveService = useCallback((service) => {
    if (!service) return null;
    if (!service.isFallback || services.length === 0) return service;
    const serviceKey = normalizeServiceKey(service.service_name);
    return services.find(item => item.id === service.id || normalizeServiceKey(item.service_name) === serviceKey) || service;
  }, [services]);

  const startServiceFlow = (service) => {
    const serviceToUse = resolveService(service);
    setSelectedService(serviceToUse); resetOrderDraft();
    const nextAction = serviceToUse.id === 'S-999' ? 'suggestion' : 'order';
    if (!user || !role) { openRoleEntry({ type: nextAction }); return; }
    setView(nextAction === 'suggestion' ? 'suggestion' : 'order');
  };

  const requireAuthView = (nextView, nextFilterStatus = null) => {
    if (!user || !role) {
      setPostAuthAction({ type: 'view', view: nextView, filterStatus: nextFilterStatus });
      setAuthMode('login'); return;
    }
    setFilterStatus(nextFilterStatus); setView(nextView);
  };

  const continueAfterAuth = useCallback(() => {
    if (!postAuthAction) return;
    if ((postAuthAction.type === 'order' || postAuthAction.type === 'suggestion') && role === 'worker') {
      setView('editProfile'); setPostAuthAction(null); return;
    }
    if (postAuthAction.type === 'order' && selectedService) {
      const serviceToUse = resolveService(selectedService);
      if (serviceToUse !== selectedService) setSelectedService(serviceToUse);
      setView('order');
    } else if (postAuthAction.type === 'suggestion') {
      setView('suggestion');
    } else if (postAuthAction.type === 'view') {
      setFilterStatus(postAuthAction.filterStatus || null); setView(postAuthAction.view);
    }
    setPostAuthAction(null);
  }, [postAuthAction, selectedService, resolveService, role]);

  const handleShare = async () => {
    const code = userData?.referral_code || user?.uid?.substring(0, 6).toUpperCase() || "FIXBOX";
    const link = `https://fixboxpa.com?ref=${code}`;
    const shareText = lang === 'es'
      ? `¡Hola! Te recomiendo FixBox, la app de reparaciones del hogar en Panamá 🔧\n\nRegistrate con mi código *${code}* y cuando hagas tu primer pedido, ¡ambos ganamos *$5* en nuestra billetera!\n\n👉 ${link}`
      : `你好！推荐你用 FixBox（巴拿马专业上门维修平台）🔧\n\n用我的邀请码 *${code}* 注册，完成首单后我们各得 *$5* 余额奖励！\n\n👉 ${link}`;
    const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'FixBox Panamá', text: shareText, url: link }); }
      catch { window.open(waUrl, '_blank'); }
    } else {
      window.open(waUrl, '_blank');
    }
  };

  const dismissInstallPrompt = (e) => {
    e.stopPropagation();
    localStorage.setItem('pwaPromptDismissed', 'true');
    setShowInstallPrompt(false);
  };

  const handleBannerClick = async () => {
    if (installDevice === 'ios') {
      showToast(lang === 'es' ? "⚠️ Instalación en iPhone" : "⚠️ iPhone 安装提示", lang === 'es' ? "Toca el ícono 'Compartir' de Safari y selecciona 'Agregar a inicio'." : "请点击浏览器底部的【分享】按钮，然后选择【添加到主屏幕】。");
    } else if (installDevice === 'android' && deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') { localStorage.setItem('pwaPromptDismissed', 'true'); setShowInstallPrompt(false); }
      setDeferredPrompt(null);
    }
  };

  // ---- 业务 handlers ----
  const normalizePhone = (raw) => String(raw || '').replace(/\D/g, '');

  const handleLogin = async () => {
    if (authPhone.trim().toLowerCase() === 'admin') {
      setAuthMode('adminLogin'); return;
    }
    const phone = normalizePhone(authPhone);
    if (phone.length < 8) return showToast(lang === 'es' ? "Celular inválido" : "手机号无效", lang === 'es' ? "Ingresa tu número de 8 dígitos" : "请输入8位手机号");
    if (!authPassword || authPassword.length < 6) return showToast(lang === 'es' ? "Contraseña muy corta" : "密码太短", lang === 'es' ? "Mínimo 6 caracteres" : "密码至少6位");
    setLoading(true);
    const fakeEmail = `${phone}@fixbox.local`;
    try { await signInWithEmailAndPassword(auth, fakeEmail, authPassword); }
    catch (err) { showToast(lang === 'es' ? "Error de acceso" : "登录失败", lang === 'es' ? "Celular o contraseña incorrectos." : "手机号或密码错误。"); setLoading(false); }
  };

  const handleRegister = async (selectedRole) => {
    const roleToCreate = selectedRole || 'client';
    if (!regData.fn || !regData.ln) return showToast(lang === 'es' ? "Nombre requerido" : "请输入姓名", lang === 'es' ? "Por favor ingresa tu nombre completo." : "请输入您的姓名后继续。");
    if (roleToCreate === 'worker' && !workerInvoiceTermsAccepted) {
      return showToast(lang === 'es' ? "Aviso" : "请先确认", lang === 'es' ? "Confirma la regla de factura para técnicos antes de continuar." : "请先确认师傅发票责任规则。");
    }
    if (!user) {
      const phone = normalizePhone(authPhone);
      if (phone.length < 8) return showToast(lang === 'es' ? "Celular inválido" : "手机号无效", lang === 'es' ? "Ingresa tu número de 8 dígitos" : "请输入8位手机号");
      if (!authPassword || authPassword.length < 6) return showToast(lang === 'es' ? "Contraseña muy corta" : "密码太短", lang === 'es' ? "Mínimo 6 caracteres" : "密码至少6位");
      if (authPassword !== authPasswordConfirm) return showToast(lang === 'es' ? "Contraseñas no coinciden" : "两次密码不一致", t.pwd_match_err);
    }
    setLoading(true);
    try {
      let uId, finalEmail, authUser = user;
      if (user) { uId = user.uid; finalEmail = user.email; }
      else {
        const phone = normalizePhone(authPhone);
        const fakeEmail = `${phone}@fixbox.local`;
        const userCred = await createUserWithEmailAndPassword(auth, fakeEmail, authPassword);
        authUser = userCred.user; uId = userCred.user.uid; finalEmail = fakeEmail;
      }
      const fullName = `${regData.fn.trim()} ${regData.ln.trim()}`;
      const referralCode = uId.substring(0, 6).toUpperCase();

      // 查找推荐人
      let referredByUid = null;
      const refCode = referralInput.trim().toUpperCase();
      if (refCode) {
        const [usersSnap, workersSnap] = await Promise.all([
          getDocs(query(collection(db, 'Users'), where('referral_code', '==', refCode))),
          getDocs(query(collection(db, 'Workers'), where('referral_code', '==', refCode))),
        ]);
        if (!usersSnap.empty) referredByUid = usersSnap.docs[0].id;
        else if (!workersSnap.empty) referredByUid = workersSnap.docs[0].id;
        else { showToast(lang === 'es' ? "Código inválido" : "邀请码无效", lang === 'es' ? "El código de invitación no existe. Déjalo en blanco si no tienes uno." : "该邀请码不存在，请检查后重试，没有邀请码可留空。"); setLoading(false); return; }
      }

      let profilePayload;
      if (roleToCreate === 'client') {
        profilePayload = { uid: uId, role: 'client', email: finalEmail, firstName: regData.fn.trim(), lastName: regData.ln.trim(), name: fullName, phone: authPhone || "", address: "", wallet_balance: 0, referral_code: referralCode, referred_by: referredByUid, createdAt: serverTimestamp() };
        await setDoc(doc(db, "Users", uId), profilePayload);
      } else {
        profilePayload = { uid: uId, role: 'worker', email: finalEmail, firstName: regData.fn.trim(), lastName: regData.ln.trim(), name: fullName, phone: authPhone || "", address: "", isVerified: false, verification_status: 'INCOMPLETE', skills: [], wallet_balance: 0, deposit_balance: 0, invoice_terms_accepted: true, invoice_terms_acceptedAt: serverTimestamp(), can_issue_invoice: false, invoice_name: fullName, invoice_ruc: "", invoice_dv: "", referral_code: referralCode, referred_by: referredByUid, createdAt: serverTimestamp() };
        await setDoc(doc(db, "Workers", uId), profilePayload);
      }
      setUser(authUser); setUserData(profilePayload); setRole(roleToCreate);
      setAuthMode(null); setWorkerInvoiceTermsAccepted(false); setReferralInput(''); setLoading(false);
      continueAfterAuth();
    } catch (e) {
      if (e.code === 'auth/email-already-in-use') showToast(lang === 'es' ? "Número ya registrado" : "号码已注册", lang === 'es' ? "Este celular ya está registrado. Por favor, inicia sesión." : "该手机号已注册，请返回登录。");
      else showToast(lang === 'es' ? "Error de registro" : "注册失败", e.message);
      setLoading(false);
    }
  };

  const handleAdminLogin = async (adminPwd) => {
    if (!adminPwd) return;
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, 'admin@fixbox.com', adminPwd);
      // Firebase session 持久化，刷新后 onAuthStateChanged 自动识别 admin 并跳转后台
      window.location.reload();
    }
    catch { showToast('Acceso denegado', 'Contraseña incorrecta'); setLoading(false); }
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    const balance = userData?.wallet_balance || 0;
    if (!withdrawAmount || isNaN(amt) || amt < 20) { showToast(lang === 'es' ? "Mínimo $20.00" : "最低提款 $20.00", lang === 'es' ? "El monto mínimo de retiro es $20." : "最低提款金额为 $20.00。"); return; }
    if (amt > balance) { showToast(lang === 'es' ? "Saldo insuficiente" : "余额不足", lang === 'es' ? "No tienes suficiente saldo para este retiro." : "您的余额不足以提取该金额。"); return; }
    const today = new Date();
    const hasWithdrawnToday = withdrawals.some(w => {
      if (!w.createdAt) return false;
      const wDate = w.createdAt.toDate();
      return wDate.getDate() === today.getDate() && wDate.getMonth() === today.getMonth() && wDate.getFullYear() === today.getFullYear();
    });
    if (hasWithdrawnToday) return showToast(lang === 'es' ? "⚠️ Límite diario alcanzado" : "⚠️ 已达每日限额", lang === 'es' ? "Solo puedes solicitar un retiro por día." : "每天仅限申请 1 次提现，您今天已经提交过了。");
    setUploading(true);
    const wId = generateID("WD");
    try {
      await setDoc(doc(db, "Withdrawals", wId), { withdraw_id: wId, user_id: user.uid, amount: amt, status: 'PENDING', createdAt: serverTimestamp() });
      const newBal = balance - amt;
      const colName = role === 'client' ? "Users" : "Workers";
      await updateDoc(doc(db, colName, user.uid), { wallet_balance: newBal });
      setUserData({ ...userData, wallet_balance: newBal });
      showToast(lang === 'es' ? "✅ Solicitud enviada" : "✅ 提现申请已提交", t.withdraw_success); setView('profile'); setWithdrawAmount("");
    } catch (e) { showToast(lang === 'es' ? "Error" : "操作失败", e.message); }
    setUploading(false);
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setUploading(true);
    try {
      const pRef = doc(db, "PromoCodes", promoInput.trim().toUpperCase());
      const pSnap = await getDoc(pRef);
      if (pSnap.exists() && pSnap.data().is_active) {
        const data = pSnap.data();
        if (data.used_by && data.used_by.includes(user.uid)) showToast(lang === 'es' ? "Código ya usado" : "已使用", t.promo_err_used);
        else { setAppliedPromo({ code: pSnap.id, discount: data.discount }); showToast(lang === 'es' ? "✅ Descuento aplicado" : "✅ 折扣已应用", t.promo_applied); }
      } else showToast(lang === 'es' ? "Código inválido" : "无效优惠码", t.promo_err_invalid);
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  const executeOrderCreation = async (initialStatus, skipNavigation = false, externalId = null) => {
    const id = externalId || generateID("FX");
    const baseAmount = selectedService.price * quantity;
    const discountAmount = appliedPromo ? appliedPromo.discount : 0;
    const serviceSubtotal = toMoney(Math.max(0, baseAmount - discountAmount));
    const taxAmount = orderInvoiceRequired ? toMoney(serviceSubtotal * INVOICE_TAX_RATE) : 0;
    const customerTotal = toMoney(serviceSubtotal + taxAmount);
    const invoicePayload = {
      invoice_required: orderInvoiceRequired, invoice_issuer: 'worker', platform_invoice: false,
      platform_receipt_only: true, invoice_status: orderInvoiceRequired ? 'REQUESTED' : 'NOT_REQUIRED',
      tax_rate: orderInvoiceRequired ? INVOICE_TAX_RATE : 0, tax_amount: taxAmount,
      billing_name: orderInvoiceRequired ? orderInvoiceDetails.name.trim() : "",
      billing_tax_id: orderInvoiceRequired ? orderInvoiceDetails.taxId.trim() : "",
      billing_dv: orderInvoiceRequired ? orderInvoiceDetails.dv.trim() : "",
      billing_email: orderInvoiceRequired ? orderInvoiceDetails.email.trim() : "",
      billing_note: orderInvoiceRequired ? orderInvoiceDetails.note.trim() : ""
    };
    try {
      await setDoc(doc(db, "Orders", id), {
        order_id: id, service_type: selectedService.service_name, amount: customerTotal,
        customer_total: customerTotal, service_subtotal: serviceSubtotal, base_amount: baseAmount,
        discount_amount: discountAmount, promo_code: appliedPromo ? appliedPromo.code : null,
        quantity, status: initialStatus, pay_method: payMethod, client_id: user.uid,
        client_no: userData?.name || user.email, client_phone: userData?.phone || authPhone || "",
        address: editData.address || userData?.address, client_photos: clientPhotos,
        client_message: clientMsg,
        client_zone: clientZone ? clientZone.id : null,
        client_zone_label: clientZone ? clientZone.label : null,
        is_urgent: orderIsUrgent,
        schedule_day: orderIsUrgent ? 'urgent' : orderScheduleDay,
        schedule_date: orderIsUrgent ? null : (orderScheduleDay === 'custom' ? orderScheduleDate : null),
        schedule_slot: orderIsUrgent ? null : orderScheduleSlot,
        ...invoicePayload, createdAt: serverTimestamp()
      });
      if (appliedPromo) await updateDoc(doc(db, "PromoCodes", appliedPromo.code), { used_by: arrayUnion(user.uid) });
      if (!userData?.address && editData.address) {
        const colName = role === 'worker' ? "Workers" : "Users";
        await setDoc(doc(db, colName, user.uid), { address: editData.address }, { merge: true });
        setUserData({ ...userData, address: editData.address });
      }
      if (!skipNavigation) {
        setView('history'); setFilterStatus(null); setClientPhotos([]); setClientMsg("");
        setPayMethod(null); setAppliedPromo(null); setPromoInput("");
        setClientZone(null); setOrderIsUrgent(false); setOrderScheduleDay('today');
        setOrderScheduleDate(''); setOrderScheduleSlot(null);
        setOrderInvoiceRequired(false); setOrderInvoiceDetails(createEmptyInvoiceDetails());
      }
    } catch (e) { showToast(lang === 'es' ? "Error al crear pedido" : "下单失败", e.message); }
    setUploading(false);
  };

  const handleFinalOrder = async () => {
    const serviceSubtotal = toMoney(Math.max(0, (selectedService.price * quantity) - (appliedPromo ? appliedPromo.discount : 0)));
    const taxAmount = orderInvoiceRequired ? toMoney(serviceSubtotal * INVOICE_TAX_RATE) : 0;
    const totalAmount = toMoney(serviceSubtotal + taxAmount);
    if (payMethod === 'wallet') {
      const balance = userData?.wallet_balance || 0;
      if (balance < totalAmount) { showToast(lang === 'es' ? "Saldo insuficiente" : "余额不足", lang === 'es' ? `Tu saldo ($${balance.toFixed(2)}) no cubre el total ($${totalAmount.toFixed(2)}).` : `您的余额 $${balance.toFixed(2)} 不足以支付 $${totalAmount.toFixed(2)}。`); return; }
      setUploading(true);
      await updateDoc(doc(db, "Users", user.uid), { wallet_balance: balance - totalAmount });
      executeOrderCreation('OPEN');
    }
  };

  const handleCancelOrder = async (order) => {
    // 注意：确认弹窗由调用方（HistoryView）处理，这里直接执行
    try {
      if (role === 'client') {
        const clientRef = doc(db, "Users", user.uid);
        const clientSnap = await getDoc(clientRef);
        let currentBal = clientSnap.exists() ? parseFloat(clientSnap.data().wallet_balance) || 0 : 0;
        const refundAmt = getOrderCustomerTotal(order);
        await updateDoc(clientRef, { wallet_balance: currentBal + refundAmt });
        await updateDoc(doc(db, "Orders", order.id), {
          status: 'REFUNDED',
          canceledAt: serverTimestamp(),
          cancel_by: 'client',
          // ACCEPTED状态下还原师傅接单记录
          ...(order.status === 'ACCEPTED' ? { techId: null, worker_pre_photos: [] } : {})
        });
        setUserData({ ...userData, wallet_balance: currentBal + refundAmt });
        // ACCEPTED状态的师傅通知：由 Cloud Function orderNotifications 触发器处理
        showToast(
          lang === 'es' ? '✅ Reembolso exitoso' : '✅ 退款成功',
          lang === 'es'
            ? `$${refundAmt.toFixed(2)} devueltos a tu billetera FixBox.`
            : `$${refundAmt.toFixed(2)} 已退回您的FixBox钱包。`
        );
      } else if (role === 'worker') {
        // Cloud Function (orderNotifications OPEN) 自动记录 abandon_count
        await updateDoc(doc(db, "Orders", order.id), {
          status: 'OPEN', techId: null, worker_pre_photos: [],
          last_abandoned_by: user.uid, last_abandonedAt: serverTimestamp(),
          abandon_reason: 'self_cancel'
        });
        showToast(lang === 'es' ? "Pedido devuelto al mercado" : "订单已退回大厅", lang === 'es' ? "El pedido está disponible para otros técnicos." : "其他师傅现在可以接这个订单了。");
      }
    } catch (error) {
      showToast('Error', error.message);
    }
  };

  const handleKickWorker = async (order) => {
    // Cloud Function 自动记录 abandon_count（abandon_reason: 'client_kick'）
    setUploading(true);
    try {
      await updateDoc(doc(db, "Orders", order.id), {
        status: 'OPEN', techId: null, worker_pre_photos: [],
        last_abandoned_by: order.techId, last_abandonedAt: serverTimestamp(),
        abandon_reason: 'client_kick'
      });
      showToast(lang === 'es' ? "Técnico removido" : "师傅已被踢出", lang === 'es' ? "El pedido volvió al mercado. Otro técnico lo tomará pronto." : "订单已重回大厅，等待新师傅接单。");
    } catch (error) { showToast("Error", error.message); }
    setUploading(false);
  };

  const handleDispute = async (order, reason) => {
    if (!reason || !reason.trim()) return;
    try {
      await updateDoc(doc(db, "Orders", order.id), { status: 'DISPUTED', dispute_reason: reason.trim(), disputedAt: serverTimestamp() });
      showToast(lang === 'es' ? "⚠️ Disputa abierta" : "⚠️ 申诉已提交", lang === 'es' ? "El soporte revisará el caso y te contactará." : "平台将冻结资金并介入处理，请耐心等待。");
    } catch (error) { showToast('Error', error.message); }
  };

  const handleAdminResolveDispute = async (order, decision) => {
    try {
      if (decision === 'refund') {
        if (!await showConfirm(lang === 'es' ? "¿Reembolsar al cliente?" : "退款给客户", lang === 'es' ? "¿Seguro que desea reembolsar el 100% al cliente?" : "确认把这笔钱全额退还给客户钱包吗？")) return;
        const clientRef = doc(db, "Users", order.client_id);
        const clientSnap = await getDoc(clientRef);
        let currentBal = parseFloat(clientSnap.data()?.wallet_balance) || 0;
        await updateDoc(clientRef, { wallet_balance: currentBal + getOrderCustomerTotal(order) });
        await updateDoc(doc(db, "Orders", order.id), { status: 'REFUNDED', refunded_to_client: getOrderCustomerTotal(order), admin_resolvedAt: serverTimestamp(), resolution: 'Refunded to Client' });
        showToast("✅ Reembolso completado", "Reembolsado al cliente. (已退款给客户)");
      } else if (decision === 'pay_worker') {
        if (!await showConfirm(lang === 'es' ? "¿Pagar al técnico?" : "打款给师傅", lang === 'es' ? "¿Seguro que desea forzar el pago al técnico? (Se cobrará comisión 10%)" : "确认强行把钱打给师傅吗？（平台照常抽成10%）")) return;
        const settlement = calculateOrderSettlement(order);
        const workerRef = doc(db, "Workers", order.techId);
        const workerSnap = await getDoc(workerRef);
        let currentBal = parseFloat(workerSnap.data()?.wallet_balance) || 0;
        await updateDoc(workerRef, { wallet_balance: currentBal + settlement.workerPayoutTotal });
        await updateDoc(doc(db, "Orders", order.id), { status: 'COMPLETED', platform_fee: settlement.platformFee, worker_service_earnings: settlement.workerServiceEarnings, worker_tax_collected: settlement.taxAmount, worker_earnings: settlement.workerPayoutTotal, admin_resolvedAt: serverTimestamp(), completedAt: serverTimestamp(), resolution: 'Paid to Worker by Admin' });
        showToast("✅ Pago forzado", `$${settlement.workerPayoutTotal.toFixed(2)} enviado al técnico. (已强制打款给师傅)`);
      } else if (decision === 'partial') {
        const refundStr = await showPrompt(lang === 'es' ? "Monto a reembolsar al cliente:" : "退款给客户金额", lang === 'es' ? "Ej: 15.00" : "例如：15.00", { type: 'number' });
        if (!refundStr) return;
        const refundAmt = parseFloat(refundStr);
        const orderAmt = getOrderCustomerTotal(order);
        if (isNaN(refundAmt) || refundAmt <= 0 || refundAmt >= orderAmt) return showToast(lang === 'es' ? "Monto inválido" : "金额无效", lang === 'es' ? "El monto debe ser mayor a $0 y menor al total del pedido." : "金额必须大于 $0 且小于订单总额。");
        if (!await showConfirm(lang === 'es' ? "¿Confirmar compensación parcial?" : "确认扣款补偿", lang === 'es' ? `¿Compensación $${refundAmt} al cliente y pagar el resto al técnico?` : `确认退给客户 $${refundAmt}，剩余按比例打给师傅？`)) return;
        const settlement = calculateOrderSettlement(order, refundAmt);
        const clientRef = doc(db, "Users", order.client_id);
        const clientSnap = await getDoc(clientRef);
        let currentClientBal = parseFloat(clientSnap.data()?.wallet_balance) || 0;
        await updateDoc(clientRef, { wallet_balance: currentClientBal + refundAmt });
        const workerRef = doc(db, "Workers", order.techId);
        const workerSnap = await getDoc(workerRef);
        let currentWorkerBal = parseFloat(workerSnap.data()?.wallet_balance) || 0;
        await updateDoc(workerRef, { wallet_balance: currentWorkerBal + settlement.workerPayoutTotal });
        await updateDoc(doc(db, "Orders", order.id), { status: 'COMPLETED', platform_fee: settlement.platformFee, worker_service_earnings: settlement.workerServiceEarnings, worker_tax_collected: settlement.taxAmount, worker_earnings: settlement.workerPayoutTotal, refunded_to_client: refundAmt, admin_resolvedAt: serverTimestamp(), completedAt: serverTimestamp(), resolution: `Partial: $${refundAmt} refunded to client` });
        showToast("✅ Resuelto", lang === 'es' ? `Cliente recibe $${refundAmt}, Técnico recibe $${settlement.workerPayoutTotal.toFixed(2)}` : `客户获赔 $${refundAmt}，师傅到手 $${settlement.workerPayoutTotal.toFixed(2)}`);
      }
    } catch (error) { showToast("Error", error.message); }
  };

  const handleApproveWorker = async (worker) => {
    if (!await showConfirm(lang === 'es' ? '¿Aprobar técnico?' : '通过认证', lang === 'es' ? '¿Aprobar a este técnico para tomar pedidos?' : '确认通过该师傅认证并允许接单？')) return;
    try {
      await updateDoc(doc(db, "Workers", worker.id), { isVerified: true, verification_status: 'APPROVED', verification_rejection: null, verification_approvedAt: serverTimestamp() });
      showToast(lang === 'es' ? "✅ Técnico aprobado" : "✅ 师傅认证已通过", lang === 'es' ? "El técnico ya puede tomar pedidos." : "师傅现在可以开始接单了。");
    } catch (error) { showToast("Error", error.message); }
  };

  const handleRejectWorker = async (worker) => {
    const reason = await showPrompt(lang === 'es' ? 'Motivo del rechazo:' : '驳回原因', lang === 'es' ? 'Motivo visible para el técnico…' : '请输入原因（师傅端可见）…');
    if (!reason || !reason.trim()) return;
    try {
      await updateDoc(doc(db, "Workers", worker.id), { isVerified: false, verification_status: 'REJECTED', verification_rejection: reason.trim(), verification_rejectedAt: serverTimestamp() });
      showToast(lang === 'es' ? "✅ Técnico rechazado" : "✅ 已驳回", lang === 'es' ? "Rechazado con motivo enviado." : "已驳回，并已记录原因。");
    } catch (error) { showToast("Error", error.message); }
  };

  const handleUnbanWorker = async (worker) => {
    try {
      await updateDoc(doc(db, "Workers", worker.id || worker.uid), {
        is_suspended: false,
        abandon_count: 0,
        self_cancel_count: 0,
        kick_count: 0,
        timeout_count: 0,
        last_abandonAt: null,
        unban_by_admin: serverTimestamp(),
      });
      showToast(lang === 'es' ? '✅ Técnico reactivado' : '✅ 解封成功', lang === 'es' ? `${worker.name} puede tomar pedidos de nuevo.` : `${worker.name} 已恢复接单资格，弃单计数已归零。`);
    } catch (error) { showToast('Error', error.message); }
  };

  const handleResubmitWorkerProfile = async () => {
    if (!await showConfirm(lang === 'es' ? '¿Reenviar perfil?' : '重新提交', lang === 'es' ? '¿Enviar de nuevo tu perfil a revisión?' : '确认重新提交资料审核？')) return;
    try {
      await updateDoc(doc(db, "Workers", user.uid), { verification_status: 'PENDING', verification_rejection: null, verification_submittedAt: serverTimestamp() });
      setUserData(prev => ({ ...prev, verification_status: 'PENDING', verification_rejection: null }));
      showToast(lang === 'es' ? "✅ Perfil reenviado" : "✅ 已重新提交", lang === 'es' ? 'Perfil enviado de nuevo a revisión.' : '已重新提交审核，请等待平台处理。');
    } catch (error) { showToast("Error", error.message); }
  };

  const getWorkerDraftPhotos = (orderId) => workerPhotoDrafts[orderId] || [];
  const setWorkerDraftPhotos = (orderId, photos) => setWorkerPhotoDrafts(prev => ({ ...prev, [orderId]: photos }));
  const clearWorkerDraftPhotos = (orderId) => setWorkerPhotoDrafts(prev => { const next = { ...prev }; delete next[orderId]; return next; });

  const handleOrderAction = async (order, currentStatus) => {
    try {
      const draftPhotos = getWorkerDraftPhotos(order.id);
      if (currentStatus === 'OPEN') {
        // 防囤单：查是否有进行中订单
        const q = query(collection(db, "Orders"), where("techId", "==", user.uid), where("status", "in", ["ACCEPTED", "WORKING", "ASSIGNED"]));
        const snap = await getDocs(q);
        if (!snap.empty) return showToast(lang === 'es' ? "⚠️ Pedido en curso" : "⚠️ 防囤单拦截", lang === 'es' ? "Termina tu pedido activo antes de tomar otro." : "请先完成当前订单再接新单。");
        // 发票资质检查
        if (order.invoice_required && !(userData?.can_issue_invoice && userData?.invoice_ruc)) {
          return showToast(lang === 'es' ? "Factura requerida" : "需要开票资质", lang === 'es' ? "Activa tu capacidad de factura en Mis Datos." : "请先在资料页开通开票资质。");
        }
        // 发票二次确认 → 由 UI 层控制（grabConfirmOrderId），此处已是确认后调用
        // 使用 Firestore Transaction 防并发抢单
        try {
          await runTransaction(db, async (tx) => {
            const orderRef = doc(db, "Orders", order.id);
            const snap = await tx.get(orderRef);
            if (!snap.exists() || snap.data().status !== 'OPEN') {
              throw new Error(lang === 'es' ? "Este pedido ya fue tomado por otro técnico." : "该订单刚刚被其他师傅抢走了。");
            }
            tx.update(orderRef, {
              techId: user.uid, tech_name: userData?.name || '',
              tech_phone: userData?.phone || '', tech_photo: userData?.avatar || userData?.selfie || null,
              status: 'ACCEPTED', worker_invoice_acknowledged: Boolean(order.invoice_required),
              worker_invoice_acknowledgedAt: order.invoice_required ? serverTimestamp() : null,
              acceptedAt: serverTimestamp()
            });
          });
          setGrabConfirmOrderId(null);
        } catch (txErr) {
          return showToast(lang === 'es' ? "No disponible" : "抢单失败", txErr.message);
        }
      } else if (currentStatus === 'ACCEPTED' || currentStatus === 'ASSIGNED') {
        if (draftPhotos.length === 0) return showToast(lang === 'es' ? "Foto requerida" : "需要照片", lang === 'es' ? "Sube al menos 1 foto antes de iniciar el trabajo." : "开工前必须上传至少1张照片。");
        await updateDoc(doc(db, "Orders", order.id), { status: 'WORKING', worker_pre_photos: draftPhotos, startedAt: serverTimestamp() });
        clearWorkerDraftPhotos(order.id);
      } else if (currentStatus === 'WORKING') {
        if (draftPhotos.length === 0) return showToast(lang === 'es' ? "Fotos requeridas" : "需要照片", lang === 'es' ? "Sube al menos 1 foto de la obra terminada." : "完工后必须上传至少1张照片。");
        const matCost = parseFloat(workerMaterialsCost) || 0;
        if (matCost > 0 && !workerMaterialsReceipt) return showToast(
          lang === 'es' ? "Recibo de materiales requerido" : "请上传材料收据",
          lang === 'es' ? "Debes subir una foto del recibo de materiales antes de finalizar." : "填写了材料费就必须上传收据照片，才能提交验收。"
        );
        await updateDoc(doc(db, "Orders", order.id), {
          status: 'PENDING_APPROVAL', worker_post_photos: draftPhotos, finishedAt: serverTimestamp(),
          materials_cost: matCost, materials_note: workerMaterialsNote.trim(),
          materials_receipt_photo: workerMaterialsReceipt || null,
        });
        clearWorkerDraftPhotos(order.id);
        setWorkerMaterialsCost(""); setWorkerMaterialsNote(""); setWorkerMaterialsReceipt(null);
      } else if (currentStatus === 'AUTO_CLAIM') {
        // 自动收款：由云函数处理，后端验证时间 + 原子操作
        const res = await fetch("https://autoclaim-3l4mofoauq-uc.a.run.app", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: order.id, techId: user.uid })
        });
        const data = await res.json();
        if (data.success) {
          showToast(lang === 'es' ? "💰 ¡Pago liberado!" : "💰 收款成功！", lang === 'es' ? `Recibirás $${data.workerPayout?.toFixed(2)} en tu billetera.` : `$${data.workerPayout?.toFixed(2)} 已入账钱包。`);
        } else {
          showToast(lang === 'es' ? "No disponible" : "暂不可用", data.error || (lang === 'es' ? "Aún no han pasado 8 horas desde que terminaste." : "距完工未满8小时，请稍后再试。"));
        }
      } else if (currentStatus === 'PENDING_APPROVAL') {
        // 客户手动验收：只更新 Orders（Cloud Function 负责结算师傅余额）
        await updateDoc(doc(db, "Orders", order.id), {
          status: 'COMPLETED',
          review_comment: reviewComment,
          rating,
          approvedAt: serverTimestamp(),
          completedAt: serverTimestamp(),
        });
        setReviewComment(""); setRating(5);
        showToast(lang === 'es' ? "💰 ¡Pago liberado!" : "💰 验收完成！", lang === 'es' ? "El técnico recibirá su pago en segundos." : "师傅即将收到结算款，感谢您的使用！");
      }
    } catch (error) {
      console.error("handleOrderAction error:", error);
      showToast(lang === 'es' ? "Error" : "操作失败", error.message || String(error));
    }
  };

  const handleExportCSV = () => {
    const pendingW = allWithdrawals.filter(w => w.status === 'PENDING');
    if (pendingW.length === 0) { showToast(lang === 'es' ? "Sin datos" : "暂无数据", lang === 'es' ? "No hay retiros pendientes para exportar." : "没有需要打款的提现申请。"); return; }
    let csvContent = "Fecha,ID Trabajador,Nombre Trabajador,Titular de Cuenta,Tipo de Cuenta,Numero de Cuenta,Monto($)\n";
    pendingW.forEach(w => {
      const worker = allWorkers.find(wk => wk.uid === w.user_id) || {};
      const date = w.createdAt ? w.createdAt.toDate().toLocaleDateString() : "";
      csvContent += `"${date}","${w.user_id}","${worker.name || 'Desconocido'}","${worker.bankInfo?.accName || 'N/A'}","${worker.bankInfo?.accType || 'N/A'}","${worker.bankInfo?.accNum || 'N/A'}","${(Number(w.amount)||0).toFixed(2)}"\n`;
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url); link.setAttribute("download", `FixBox_ACH_Pagos_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden'; document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleSaveProfile = async () => {
    const expectedName = normalizeString(`${editData.firstName} ${editData.lastName}`);
    const enteredBankName = normalizeString(editData.accName || "");
    const isVerifiedWorker = role === 'worker' && userData?.isVerified;
    const editBankNameMatches = !enteredBankName || expectedName === enteredBankName;

    if (role === 'worker' && !isVerifiedWorker) {
      const editIdentityComplete = Boolean(editData.firstName.trim() && editData.lastName.trim() && editData.phone.trim() && editData.address.trim());
      const editSkillsComplete = Boolean(editData.skills && editData.skills.length > 0);
      const editInvoiceTermsComplete = Boolean(editData.invoiceTermsAccepted);
      const editBankComplete = Boolean(editData.accName.trim() && editData.accNum.trim() && editData.accType && editBankNameMatches);
      const editDocsComplete = Boolean(editData.idPhoto && editData.selfie);
      const workerEditReady = editIdentityComplete && editSkillsComplete && editInvoiceTermsComplete && editBankComplete && editDocsComplete;
      if (!workerEditReady) {
        const missing = [];
        if (!editIdentityComplete) missing.push(lang === 'es' ? 'Datos' : '身份资料');
        if (!editSkillsComplete) missing.push(lang === 'es' ? 'Servicios' : '服务类型');
        if (!editInvoiceTermsComplete) missing.push(lang === 'es' ? 'Factura' : '发票规则');
        if (!editBankComplete) missing.push(lang === 'es' ? 'Cobro' : '收款账户');
        if (!editDocsComplete) missing.push(lang === 'es' ? 'Fotos' : '证件照片');
        return showToast(lang === 'es' ? "Perfil incompleto" : "资料不完整", lang === 'es' ? `Falta completar: ${missing.join(', ')}` : `请先完成：${missing.join('、')}`);
      }
    }
    if (role === 'worker' && editData.canIssueInvoice && !editData.invoiceRuc.trim()) {
      return showToast(lang === 'es' ? "Dato requerido" : "信息缺失", lang === 'es' ? 'Completa RUC o cédula para factura.' : '请填写 RUC 或身份证号码。');
    }
    if (!isVerifiedWorker && editData.accName && normalizeString(`${editData.firstName} ${editData.lastName}`) && !editBankNameMatches) {
      return showToast(lang === 'es' ? "Error de cuenta" : "账户核验失败", t.bank_match_err);
    }
    setUploading(true);
    try {
      const colName = role === 'client' ? "Users" : "Workers";
      let payload = {
        firstName: editData.firstName.trim(), lastName: editData.lastName.trim(),
        name: `${editData.firstName.trim()} ${editData.lastName.trim()}`,
        address: editData.address, phone: editData.phone,
        bankInfo: { bank: "Banco General", accName: editData.accName, accNum: editData.accNum, accType: editData.accType }
      };
      if (editData.avatar) payload.avatar = editData.avatar;
      if (role === 'worker') {
        payload.reference = editData.reference;
        if (editData.idPhoto) payload.idPhoto = editData.idPhoto;
        if (editData.selfie) payload.selfie = editData.selfie;
        if (editData.avatar) payload.avatar = editData.avatar;
        payload.skills = [...new Set((editData.skills || []).map(skill => String(skill || '').trim()).filter(Boolean))];
        payload.coverage_zones = editData.coverageZones || [];
        payload.invoice_terms_accepted = Boolean(editData.invoiceTermsAccepted);
        if (editData.invoiceTermsAccepted && !userData?.invoice_terms_accepted) payload.invoice_terms_acceptedAt = serverTimestamp();
        payload.can_issue_invoice = Boolean(editData.canIssueInvoice);
        payload.invoice_name = (editData.invoiceName || `${editData.firstName.trim()} ${editData.lastName.trim()}`).trim();
        payload.invoice_ruc = (editData.invoiceRuc || "").trim();
        payload.invoice_dv = (editData.invoiceDv || "").trim();
        const isVerifiedWorker = role === 'worker' && userData?.isVerified;
        if (!isVerifiedWorker) { payload.verification_status = 'PENDING'; payload.verification_rejection = null; payload.verification_submittedAt = serverTimestamp(); }
      }
      await setDoc(doc(db, colName, user.uid), payload, { merge: true });
      setUserData(prev => ({ ...prev, ...payload }));
      setUploading(false);
      showToast(lang === 'es' ? "✅ Guardado correctamente" : "✅ 修改已保存", ""); setView('profile');
    } catch (error) { setUploading(false); showToast(lang === 'es' ? "Error" : "保存失败", error.message); }
  };

  // ---- useEffect: 定时器 ----
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // ---- useEffect: PWA 安装提示 ----
  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeSplash(true), 1500);
    const removeTimer = setTimeout(() => setShowSplash(false), 2000);
    const isIos = () => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); setDeferredPrompt(e); setInstallDevice('android');
      if (!localStorage.getItem('pwaPromptDismissed')) setTimeout(() => setShowInstallPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    if (!isStandalone && !localStorage.getItem('pwaPromptDismissed') && isIos()) {
      setInstallDevice('ios'); setTimeout(() => setShowInstallPrompt(true), 3000);
    }
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt); };
  }, []);

  // ---- useEffect: Auth 监听 ----
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        // ── Admin 账号直接进后台 ──
        if (u.email === 'admin@fixbox.com') {
          setRole('admin'); setUserData({ name: 'Admin', email: u.email });
          setAuthMode(null); setView('admin');
          setLoading(false); return;
        }
        let d = await getDoc(doc(db, "Users", u.uid));
        if (d.exists()) {
          setUserData(d.data()); setRole('client');
          const data = d.data();
          setEditData({ firstName: data.firstName || "", lastName: data.lastName || "", phone: data.phone || "", address: data.address || "", accName: data.bankInfo?.accName || "", accNum: data.bankInfo?.accNum || "", accType: data.bankInfo?.accType || "ahorro", reference: "", idPhoto: null, selfie: null, avatar: data.avatar || null, skills: [], invoiceTermsAccepted: false, canIssueInvoice: false, invoiceName: "", invoiceRuc: "", invoiceDv: "", coverageZones: [] });
        } else {
          d = await getDoc(doc(db, "Workers", u.uid));
          if (d.exists()) {
            setUserData(d.data()); setRole('worker');
            const data = d.data();
            setEditData({ firstName: data.firstName || "", lastName: data.lastName || "", phone: data.phone || "", address: data.address || "", accName: data.bankInfo?.accName || "", accNum: data.bankInfo?.accNum || "", accType: data.bankInfo?.accType || "ahorro", reference: data.reference || "", idPhoto: data.idPhoto || null, selfie: data.selfie || null, avatar: data.avatar || null, skills: data.skills || [], invoiceTermsAccepted: Boolean(data.invoice_terms_accepted), canIssueInvoice: Boolean(data.can_issue_invoice), invoiceName: data.invoice_name || data.name || "", invoiceRuc: data.invoice_ruc || "", invoiceDv: data.invoice_dv || "", coverageZones: data.coverage_zones || [] });
          }
        }
      } else { setUser(null); setUserData(null); setRole(null); setAuthMode(null); setView('home'); }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ---- useEffect: 实时用户数据 ----
  useEffect(() => {
    if (!user || !role) return;
    const colName = role === 'client' ? "Users" : "Workers";
    const unsubscribe = onSnapshot(doc(db, colName, user.uid), (snap) => {
      if (snap.exists()) setUserData(snap.data());
    });
    return () => unsubscribe();
  }, [user, role]);

  // ---- useEffect: FCM 推送通知 ----
  useEffect(() => {
    if (!user || !role || !messaging) return;
    const requestNotificationPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const token = await getToken(messaging, { vapidKey: 'BIHrGa2mKmIxbn_yLSIqRu5A3rmqdBsCMBT3EGU4pYA2OENA7TQmG5cUeizIpbsLdddg8JXjYgBqsOwjO72leMI' });
          if (token) {
            // 存到 FCMTokens/{uid} — 云函数从这里读
            await setDoc(doc(db, "FCMTokens", user.uid), { token, updatedAt: serverTimestamp() });
          }
        }
      } catch (error) { console.log("FCM Token error:", error); }
    };
    requestNotificationPermission();
    const unsubscribeMsg = onMessage(messaging, (payload) => {
      showToast(payload.notification?.title || "Nueva Notificación", payload.notification?.body || "");
    });
    return () => unsubscribeMsg();
  }, [user, role]);

  // ---- useEffect: 公共数据订阅（服务、Banner、法律文本）----
  useEffect(() => {
    const unsubscribers = [];
    const watch = (fn) => { const u = fn(); if (typeof u === 'function') unsubscribers.push(u); };
    watch(() => onSnapshot(query(collection(db, "Services")), s => {
      let servs = s.docs.map(d => ({ id: d.id, ...d.data() }));
      if (!isAdmin) servs = servs.filter(srv => srv.is_active !== false);
      setServices(servs);
    }));
    watch(() => onSnapshot(collection(db, "Banners"), s => {
      const data = {}; s.docs.forEach(d => data[d.id] = d.data()); setBanners(data);
    }));
    watch(() => onSnapshot(doc(db, "PlatformSettings", "legalText"), (docSnap) => {
      if (docSnap.exists()) { setLegalContent(docSnap.data()); setAdminLegal(docSnap.data()); }
    }));
    return () => unsubscribers.forEach(u => u());
  }, [isAdmin]);

  // ---- useEffect: 用户数据订阅（订单、提现等）----
  useEffect(() => {
    if (!user) return;
    const unsubscribers = [];
    const watch = (fn) => { const u = fn(); if (typeof u === 'function') unsubscribers.push(u); };

    const orderBuckets = {};
    const activeWorkerSkills = workerSkillKey ? workerSkillKey.split('|').map(normalizeServiceKey).filter(Boolean) : [];
    const publishOrders = () => {
      const merged = new Map();
      Object.values(orderBuckets).flat().forEach(order => merged.set(order.id, order));
      setOrders([...merged.values()].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
    };
    const notifyOrderChanges = (s, isInitial) => {
      if (isInitial) return;
      s.docChanges().forEach(change => {
        const docData = change.doc.data();
        if (change.type === 'added' && role === 'worker' && docData.status === 'OPEN') {
          if (activeWorkerSkills.includes(normalizeServiceKey(docData.service_type))) {
            showToast(lang === 'es' ? "🔔 ¡Nuevo Pedido!" : "🔔 有新订单啦！", lang === 'es' ? "Hay un nuevo trabajo de tus especialidades." : "大厅掉落了您擅长的服务订单，快去抢！");
          }
        }
        if (change.type === 'modified') {
          if (role === 'client' && docData.client_id === user.uid) {
            if (docData.status === 'ACCEPTED') showToast(lang === 'es' ? "👨‍🔧 Técnico Asignado" : "👨‍🔧 师傅已接单", lang === 'es' ? "Un técnico ha aceptado tu pedido y está en camino." : "您的订单已被接下，师傅正在路上。");
            if (docData.status === 'WORKING') showToast(lang === 'es' ? "🛠️ Trabajo Iniciado" : "🛠️ 开始工作", lang === 'es' ? "El técnico acaba de empezar el trabajo." : "师傅已上传照片并开始工作。");
            if (docData.status === 'PENDING_APPROVAL') showToast(lang === 'es' ? "✅ Trabajo Terminado" : "✅ 完工待验收", lang === 'es' ? "El técnico terminó. Por favor aprueba el pago." : "师傅已完工，请您确认验收并打款。");
            if (docData.status === 'REFUNDED') showToast(lang === 'es' ? "⚠️ Pedido Cancelado" : "⚠️ 订单已取消", lang === 'es' ? "Se ha cancelado y reembolsado el dinero a tu billetera." : "订单已取消，全额已秒退至您的钱包余额。");
            if (docData.status === 'DISPUTED') showToast(lang === 'es' ? "⚠️ Disputa Abierta" : "⚠️ 维权申诉已开启", lang === 'es' ? "Tu dinero ha sido retenido por el soporte." : "订单资金已被冻结，等待平台介入。");
          }
          if (role === 'worker' && docData.techId === user.uid && docData.status === 'DISPUTED') {
            showToast(lang === 'es' ? "🚨 ALERTA DE DISPUTA" : "🚨 客户发起申诉", lang === 'es' ? "El cliente no está satisfecho. El pago está retenido temporalmente." : "客户不满意并已发起维权，这笔款项已被平台暂时冻结！");
          }
        }
      });
    };
    const watchOrders = (key, qRef) => {
      let isInitial = true;
      watch(() => onSnapshot(qRef, s => {
        orderBuckets[key] = s.docs.map(d => ({ id: d.id, ...d.data() }));
        publishOrders(); notifyOrderChanges(s, isInitial); isInitial = false;
      }));
    };

    if (isAdmin) {
      watchOrders('adminOrders', query(collection(db, "Orders"), orderBy("createdAt", "desc"), queryLimit(ADMIN_ORDER_LIMIT)));
    } else if (role === 'client') {
      watchOrders('clientOrders', query(collection(db, "Orders"), where("client_id", "==", user.uid), queryLimit(CLIENT_ORDER_LIMIT)));
    } else if (role === 'worker') {
      watchOrders('workerOrders', query(collection(db, "Orders"), where("techId", "==", user.uid), queryLimit(CLIENT_ORDER_LIMIT)));
      if (userData?.isVerified && activeWorkerSkills.length > 0) {
        watchOrders('marketOrders', query(collection(db, "Orders"), where("status", "==", "OPEN"), queryLimit(WORKER_MARKET_LIMIT)));
      }
    }

    if (role) {
      watch(() => onSnapshot(query(collection(db, "Withdrawals"), where("user_id", "==", user.uid)), s => {
        let wData = s.docs.map(d => ({ id: d.id, ...d.data() }));
        wData.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        setWithdrawals(wData);
      }));
    }

    if (isAdmin) {
      watch(() => onSnapshot(query(collection(db, "Withdrawals")), s => {
        let allW = s.docs.map(d => ({ id: d.id, ...d.data() }));
        allW.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        setAllWithdrawals(allW);
      }));
      watch(() => onSnapshot(collection(db, "Workers"), s => setAllWorkers(s.docs.map(d => ({ id: d.id, ...d.data() })))));
      watch(() => onSnapshot(collection(db, "Users"), s => setAllClients(s.docs.map(d => ({ id: d.id, ...d.data() })))));
      watch(() => onSnapshot(collection(db, "Suggestions"), s => {
        let suggs = s.docs.map(d => ({ id: d.id, ...d.data() }));
        suggs.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        setSuggestions(suggs);
      }));
      watch(() => onSnapshot(collection(db, "PromoCodes"), s => setPromoCodes(s.docs.map(d => ({ id: d.id, ...d.data() })))));
    }

    return () => unsubscribers.forEach(u => u());
  }, [role, user, isAdmin, lang, workerSkillKey, userData?.isVerified]);

  // ---- useEffect: 师傅超时自动踢出 ----
  useEffect(() => {
    if (role !== 'client' || orders.length === 0) return;
    const hardTimeoutMinutes = 120;
    orders.forEach(async (o) => {
      if (o.status === 'ACCEPTED' && o.client_id === user.uid && o.acceptedAt) {
        const acceptedTime = o.acceptedAt.toDate();
        const diffMinutes = (currentTime - acceptedTime) / (1000 * 60);
        if (diffMinutes >= hardTimeoutMinutes) {
          try {
            await updateDoc(doc(db, "Orders", o.id), {
              status: 'OPEN', techId: null, worker_pre_photos: [],
              last_abandoned_by: o.techId, last_abandonedAt: serverTimestamp(),
              abandon_reason: 'timeout_auto'
            });
          } catch (e) { console.error("Auto-kick error:", e); }
        }
      }
    });
  }, [currentTime, orders, role, user]);

  // ---- useEffect: 登录后跳转 ----
  useEffect(() => {
    if (user && !role && !loading && authMode === 'login') {
      setAuthMode('role');
      if (user.displayName) {
        const parts = user.displayName.split(' ');
        setRegData({ fn: parts[0] || '', ln: parts.slice(1).join(' ') || '' });
      }
    }
  }, [user, role, loading, authMode]);

  useEffect(() => {
    if (user && role && authMode === 'login') { setAuthMode(null); continueAfterAuth(); }
  }, [user, role, authMode, continueAfterAuth]);

  useEffect(() => {
    const serviceToUse = resolveService(selectedService);
    if (serviceToUse && serviceToUse !== selectedService) setSelectedService(serviceToUse);
  }, [selectedService, resolveService]);

  // ---- Yappy Web Component 事件监听 ----
  useEffect(() => {
    if (view !== 'payment' || payMethod !== 'yappy') return;
    const btnYappy = document.getElementById('yappy-button');
    if (!btnYappy) return;

    const handleYappyClick = async () => {
      if (!userData?.address && !editData.address) {
        showToast(lang === 'es' ? "Dirección requerida" : "请填写地址", lang === 'es' ? "Por favor, ingresa tu dirección antes de pagar." : "请在上方填写服务地址。"); return;
      }
      if (orderInvoiceRequired && !(orderInvoiceDetails.name.trim() && orderInvoiceDetails.taxId.trim() && orderInvoiceDetails.email.trim())) {
        showToast(lang === 'es' ? "Faltan datos de factura" : "发票信息不完整", lang === 'es' ? "Completa los datos de factura antes de pagar." : "请先填写发票资料。"); return;
      }
      const cleanYappyPhone = yappyPhone.replace(/\D/g, '');
      if (cleanYappyPhone.length < 7) {
        showToast(lang === 'es' ? 'Número Yappy inválido' : 'Yappy号码无效', lang === 'es' ? 'Ingresa tu número Yappy de 7-8 dígitos.' : '请输入7-8位Yappy手机号码。'); return;
      }
      setUploading(true);
      try {
        const serviceSubtotal = toMoney(Math.max(0, ((selectedService?.price || 0) * quantity) - (appliedPromo ? appliedPromo.discount : 0)));
        const taxAmount = orderInvoiceRequired ? toMoney(serviceSubtotal * INVOICE_TAX_RATE) : 0;
        const totalAmount = toMoney(serviceSubtotal + taxAmount);
        const orderId = "FX" + Date.now().toString().slice(-11);
        const response = await fetch("https://getyappysession-3l4mofoauq-uc.a.run.app", {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId, paymentMethod: payMethod, total: totalAmount, aliasYappy: cleanYappyPhone })
        });
        const data = await response.json();
        if (data.success && data.body) {
          executeOrderCreation('AWAITING_VERIFICATION', true, orderId);
          const params = { transactionId: data.body.transactionId || orderId, documentName: data.body.documentName || "document", token: data.body.token };
          btnYappy.eventPayment(params);
          setTimeout(() => setYappySimulating(false), 5 * 60 * 1000);
        } else {
          showToast("Error Yappy", data.detail?.status?.description || (lang === 'es' ? "No se pudo generar el token." : "无法生成支付令牌。"));
        }
      } catch (error) { showToast(lang === 'es' ? "Error de red" : "网络错误", error.message); }
      finally { setUploading(false); }
    };

    const handleYappySuccess = (event) => {
      const serviceSubtotal = toMoney(Math.max(0, ((selectedService?.price || 0) * quantity) - (appliedPromo ? appliedPromo.discount : 0)));
      const taxAmount = orderInvoiceRequired ? toMoney(serviceSubtotal * INVOICE_TAX_RATE) : 0;
      setYappySuccessData({ serviceName: selectedService?.service_name || "", amount: toMoney(serviceSubtotal + taxAmount), phone: yappyPhone, date: new Date().toLocaleString() });
      setYappyPhone('');
      setFilterStatus(null); setClientPhotos([]); setClientMsg(''); setPayMethod(null); setAppliedPromo(null); setPromoInput(''); setOrderInvoiceRequired(false); setOrderInvoiceDetails(createEmptyInvoiceDetails());
    };

    const handleYappyError = () => { setYappySimulating(false); showToast(lang === 'es' ? "Pago no completado" : "支付未完成", lang === 'es' ? "El pago fue cancelado o expiró." : "支付已取消或超时。"); };

    btnYappy.addEventListener('eventClick', handleYappyClick);
    btnYappy.addEventListener('eventSuccess', handleYappySuccess);
    btnYappy.addEventListener('eventError', handleYappyError);
    return () => {
      btnYappy.removeEventListener('eventClick', handleYappyClick);
      btnYappy.removeEventListener('eventSuccess', handleYappySuccess);
      btnYappy.removeEventListener('eventError', handleYappyError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, payMethod, selectedService, quantity, appliedPromo, lang, userData, editData, orderInvoiceRequired, orderInvoiceDetails, yappyPhone]);

  const value = {
    // 语言
    lang, setLang, t,
    // 用户
    user, setUser, userData, setUserData, role, setRole, loading, isAdmin,
    // 导航
    view, setView, filterStatus, setFilterStatus,
    // 订单创建
    selectedService, setSelectedService, clientPhotos, setClientPhotos, clientMsg, setClientMsg,
    quantity, setQuantity, payMethod, setPayMethod,
    orderInvoiceRequired, setOrderInvoiceRequired, orderInvoiceDetails, setOrderInvoiceDetails,
    clientZone, setClientZone, orderIsUrgent, setOrderIsUrgent,
    orderScheduleDay, setOrderScheduleDay, orderScheduleDate, setOrderScheduleDate,
    orderScheduleSlot, setOrderScheduleSlot,
    promoInput, setPromoInput, appliedPromo, setAppliedPromo,
    suggestionText, setSuggestionText,
    // 钱包
    withdrawAmount, setWithdrawAmount, withdrawals,
    // 编辑资料
    editData, setEditData,
    // 管理
    allWithdrawals, allWorkers, allClients, suggestions, promoCodes,
    newPromoCode, setNewPromoCode, newPromoDiscount, setNewPromoDiscount,
    newServiceName, setNewServiceName, newServicePrice, setNewServicePrice,
    dashboardTime, setDashboardTime, adminOrderFilter, setAdminOrderFilter,
    adminOrderSearch, setAdminOrderSearch, adminLegal, setAdminLegal,
    // 工单
    workerPhotoDrafts, getWorkerDraftPhotos, setWorkerDraftPhotos, clearWorkerDraftPhotos,
    reviewComment, setReviewComment, rating, setRating,
    workerMaterialsCost, setWorkerMaterialsCost, workerMaterialsNote, setWorkerMaterialsNote,
    workerMaterialsReceipt, setWorkerMaterialsReceipt,
    // 认证
    authMode, setAuthMode, postAuthAction, setPostAuthAction,
    authPhone, setAuthPhone, authPassword, setAuthPassword,
    authPasswordConfirm, setAuthPasswordConfirm, regData, setRegData,
    referralInput, setReferralInput,
    workerInvoiceTermsAccepted, setWorkerInvoiceTermsAccepted,
    grabConfirmOrderId, setGrabConfirmOrderId,
    // UI 状态
    toast, setToast, previewImage, setPreviewImage, uploading, setUploading,
    showSplash, fadeSplash, showInstallPrompt, installDevice,
    yappySimulating, setYappySimulating, yappyPhone, setYappyPhone,
    yappySuccessData, setYappySuccessData, legalContent,
    // 数据
    services, orders, banners,
    // 时间
    currentTime,
    // Handlers
    handleLogin, handleRegister, handleAdminLogin, handleWithdraw, handleApplyPromo,
    handleFinalOrder, handleCancelOrder, handleKickWorker, handleDispute,
    handleOrderAction, handleAdminResolveDispute, handleApproveWorker, handleRejectWorker, handleUnbanWorker,
    handleResubmitWorkerProfile, handleSaveProfile, handleShare, handleExportCSV,
    handleBannerClick, dismissInstallPrompt,
    executeOrderCreation,
    // 导航辅助
    openLoginFlow, openRoleEntry, beginRegistrationAs, chooseAuthRole,
    closeAuthFlow, requireAuthView, continueAfterAuth, startServiceFlow,
    showToast, showConfirm, showPrompt, compressImage, resolveService,
    // 常量/工具（从 constants 透传）
    statusMap, getWorkerReviewStatus, getWorkerReviewTone, getStatusTone,
    PANAMA_ZONES,
    normalizeServiceKey, openWhatsApp, PLATFORM_WA,
  };

  return (
    <AppContext.Provider value={value}>
      {children}

      {/* ── 全局确认弹窗（替代 window.confirm）── */}
      {confirmDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '17px', color: '#17202A', fontWeight: '900' }}>{confirmDialog.title}</h3>
            <p style={{ margin: '0 0 22px 0', fontSize: '14px', color: '#697586', lineHeight: '1.6' }}>{confirmDialog.msg}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => { confirmDialog.resolveFn(false); setConfirmDialog(null); }}
                style={{ padding: '13px', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Cancelar' : '取消'}
              </button>
              <button onClick={() => { confirmDialog.resolveFn(true); setConfirmDialog(null); }}
                style={{ padding: '13px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Confirmar' : '确认'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 全局输入弹窗（替代 window.prompt）── */}
      {promptDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: '17px', color: '#17202A', fontWeight: '900' }}>{promptDialog.title}</h3>
            {promptDialog.type === 'number' ? (
              <input
                autoFocus
                type="number"
                value={promptInput}
                onChange={e => setPromptInput(e.target.value)}
                placeholder={promptDialog.placeholder}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #D8DEE8', fontSize: '17px', boxSizing: 'border-box', outline: 'none', marginBottom: '16px', fontWeight: '700' }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && promptInput.trim()) { promptDialog.resolveFn(promptInput); setPromptDialog(null); setPromptInput(''); }
                  if (e.key === 'Escape') { promptDialog.resolveFn(null); setPromptDialog(null); setPromptInput(''); }
                }}
              />
            ) : (
              <textarea
                autoFocus
                value={promptInput}
                onChange={e => setPromptInput(e.target.value)}
                placeholder={promptDialog.placeholder}
                style={{ width: '100%', height: '90px', padding: '12px 14px', borderRadius: '8px', border: '1px solid #D8DEE8', fontSize: '14px', boxSizing: 'border-box', outline: 'none', marginBottom: '16px', resize: 'none', fontFamily: 'inherit', lineHeight: '1.5' }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { promptDialog.resolveFn(null); setPromptDialog(null); setPromptInput(''); }
                }}
              />
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => { promptDialog.resolveFn(null); setPromptDialog(null); setPromptInput(''); }}
                style={{ padding: '13px', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Cancelar' : '取消'}
              </button>
              <button disabled={!promptInput.trim()} onClick={() => { promptDialog.resolveFn(promptInput); setPromptDialog(null); setPromptInput(''); }}
                style={{ padding: '13px', background: promptInput.trim() ? '#2563EB' : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: promptInput.trim() ? 'pointer' : 'default' }}>
                {lang === 'es' ? 'Confirmar' : '确认'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppContext.Provider>
  );
}
